import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Trophy, Flame, TrendingUp, Dumbbell, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StrengthProgressMessage
 * 
 * Muestra un mensaje motivacional automático cuando el cliente
 * lleva 3+ semanas de entrenamiento, indicando su mejora en
 * tren superior e inferior.
 */
const StrengthProgressMessage = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        // Verificar si ya se mostró hoy
        const lastShown = localStorage.getItem('strength_message_shown');
        const today = new Date().toDateString();
        
        if (lastShown === today) {
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API}/me/strength-progress-message`);
        
        if (response.data.show_message) {
          setData(response.data);
          setShow(true);
          // Marcar como mostrado hoy
          localStorage.setItem('strength_message_shown', today);
        }
      } catch (error) {
        console.error('Error fetching strength progress:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, []);

  const handleClose = () => {
    setShow(false);
    onClose?.();
  };

  if (loading || !show || !data) {
    return null;
  }

  return (
    <Dialog open={show} onOpenChange={handleClose}>
      <DialogContent className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-primary/20 border-primary/30 max-w-md p-0 overflow-hidden">
        {/* Header con efecto */}
        <div className="relative bg-gradient-to-r from-primary/20 to-yellow-500/20 p-6 pb-8">
          <button 
            onClick={handleClose}
            className="absolute top-3 right-3 text-zinc-400 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-yellow-500/30 rounded-full blur-xl animate-pulse" />
              <div className="relative bg-gradient-to-br from-yellow-500 to-orange-500 p-4 rounded-full">
                <Trophy className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>
          
          <h2 className="text-xl font-bold text-white text-center">
            ¡Progreso Detectado!
          </h2>
          <p className="text-sm text-zinc-400 text-center mt-1">
            Semana {data.current_week} de tu plan
          </p>
        </div>

        {/* Contenido */}
        <div className="p-6 pt-4">
          {/* Mensaje principal */}
          <p className="text-lg text-white text-center leading-relaxed mb-6">
            {data.message}
          </p>

          {/* Stats */}
          {data.type === 'progress' && (data.superior_improvement > 0 || data.inferior_improvement > 0) && (
            <div className="grid grid-cols-2 gap-3 mb-6">
              {data.inferior_improvement > 0 && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Dumbbell className="h-5 w-5 text-green-400" />
                    <span className="text-xs text-green-300 uppercase tracking-wide">Tren Inferior</span>
                  </div>
                  <p className="text-3xl font-bold text-green-400">
                    +{data.inferior_improvement}%
                  </p>
                  <p className="text-xs text-green-300/70 mt-1">más fuerte</p>
                </div>
              )}
              
              {data.superior_improvement > 0 && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-blue-400" />
                    <span className="text-xs text-blue-300 uppercase tracking-wide">Tren Superior</span>
                  </div>
                  <p className="text-3xl font-bold text-blue-400">
                    +{data.superior_improvement}%
                  </p>
                  <p className="text-xs text-blue-300/70 mt-1">más fuerte</p>
                </div>
              )}
            </div>
          )}

          {/* Mensaje motivacional */}
          <div className="bg-zinc-800/50 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Flame className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-zinc-300">
                {data.type === 'progress' 
                  ? "Tu cuerpo está respondiendo al entrenamiento. ¡Mantén la constancia y verás resultados aún mayores!"
                  : "La constancia es la madre de los resultados. ¡Cada sesión cuenta!"}
              </p>
            </div>
          </div>

          {/* Botón */}
          <Button 
            onClick={handleClose}
            className="w-full bg-gradient-to-r from-primary to-yellow-500 hover:from-primary/90 hover:to-yellow-500/90 text-white font-bold py-6"
          >
            ¡A por la sesión de hoy! 💪
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StrengthProgressMessage;
